import React, { useState, useEffect } from 'react'
import { gql } from 'graphql-request'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4050'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { Target, Plus, Trash2, Pencil, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/useToast'
import type { OrganisationWithDetails, Module } from '../index'
import CreateTargetModal from './CreateTargetModal'

interface TargetsTabProps {
  organisation: OrganisationWithDetails | null
  modules: Module[]
  loading: boolean
  onRefresh: () => void
}

export interface TargetItem {
  id: number
  moduleId: number
  organisationId: number
  payloadId: number
  payloadName: string
  coachPayloadId: number | null
  elevenlabsVoiceId: string | null
  avatarId: number | null
  avatar: { id: number; name: string } | null
  minRating: number
  minSessions: number
  createdAt: string
  updatedAt: string
}

const GET_ORGANISATION_TARGETS = gql`
  query getOrganisationTargets($organisationId: Int!) {
    getOrganisationTargets(organisationId: $organisationId) {
      id
      moduleId
      organisationId
      payloadId
      payloadName
      coachPayloadId
      elevenlabsVoiceId
      avatarId
      avatar {
        id
        name
      }
      minRating
      minSessions
      createdAt
      updatedAt
    }
  }
`

const DELETE_TARGET = gql`
  mutation deleteTarget($targetId: Int!) {
    deleteTarget(targetId: $targetId) {
      success
      message
    }
  }
`

const CLEAR_TARGET_WELCOME_CACHE = gql`
  mutation clearTargetWelcomeCache($targetId: Int!) {
    clearTargetWelcomeCache(targetId: $targetId) {
      success
      message
    }
  }
`

const TargetsTab: React.FC<TargetsTabProps> = ({ organisation, modules, loading, onRefresh }) => {
  const [targets, setTargets] = useState<TargetItem[]>([])
  const [loadingTargets, setLoadingTargets] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingTarget, setEditingTarget] = useState<TargetItem | null>(null)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [clearingCacheId, setClearingCacheId] = useState<number | null>(null)
  const { showSuccess, showError } = useToast()

  useEffect(() => {
    if (organisation?.id) {
      fetchTargets()
    }
  }, [organisation?.id])

  const fetchTargets = async () => {
    if (!organisation) return

    try {
      setLoadingTargets(true)
      const response = await authorizedGraphQLRequest<{
        getOrganisationTargets: TargetItem[]
      }>(GET_ORGANISATION_TARGETS, { organisationId: organisation.id })
      setTargets(response.getOrganisationTargets || [])
    } catch (error) {
      console.error('Failed to fetch targets:', error)
    } finally {
      setLoadingTargets(false)
    }
  }

  const handleDelete = async (targetId: number) => {
    if (!confirm('Are you sure you want to delete this target?')) return

    try {
      setDeletingId(targetId)
      await authorizedGraphQLRequest(DELETE_TARGET, { targetId })
      await fetchTargets()
    } catch (error) {
      console.error('Failed to delete target:', error)
    } finally {
      setDeletingId(null)
    }
  }

  const handleClearCache = async (targetId: number) => {
    // Non-destructive: only forces the welcome to regenerate on next open, so no confirm needed.
    try {
      setClearingCacheId(targetId)
      await authorizedGraphQLRequest(CLEAR_TARGET_WELCOME_CACHE, { targetId })
      showSuccess('Welcome cache cleared — it will regenerate on next open')
    } catch (error) {
      console.error('Failed to clear target welcome cache:', error)
      showError('Failed to clear welcome cache')
    } finally {
      setClearingCacheId(null)
    }
  }

  const handleModalSuccess = () => {
    setIsModalOpen(false)
    setEditingTarget(null)
    fetchTargets()
    onRefresh()
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setEditingTarget(null)
  }

  const handleEdit = (target: TargetItem) => {
    setEditingTarget(target)
    setIsModalOpen(true)
  }

  const handleAddNew = () => {
    setEditingTarget(null)
    setIsModalOpen(true)
  }

  const getModuleName = (moduleId: number): string => {
    const module = modules.find((m) => m.id === moduleId)
    return module?.name || `Module ${moduleId}`
  }

  if (loading || loadingTargets) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!organisation) {
    return <div className="text-center text-gray-500 py-12">No organisation selected</div>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h4 className="font-semibold text-gray-800">Targets ({targets.length})</h4>
        <Button onClick={handleAddNew} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Add Target
        </Button>
      </div>

      {/* Targets List */}
      {targets.length === 0 ? (
        <div className="text-center text-gray-500 py-8 bg-gray-50 rounded-xl">
          <Target className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p>No targets configured</p>
          <p className="text-sm mt-1">Add targets to define completion criteria for users</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Module</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payload</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avatar</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Voice ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Min Rating</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Min Sessions</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {targets.map((target) => (
                  <tr key={target.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Target className="h-4 w-4 text-primary" />
                        <span className="font-medium text-gray-900">{getModuleName(target.moduleId)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className="font-medium text-gray-900">{target.payloadName}</span>
                      <span className="text-xs text-gray-400 ml-1">(ID: {target.payloadId})</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {target.avatar ? (
                        <div className="flex items-center gap-2">
                          <img
                            src={`${API_URL}/api/avatars/${target.avatar.id}/image`}
                            alt=""
                            className="w-6 h-6 rounded-full object-cover"
                            onError={(e) => {
                              ;(e.target as HTMLImageElement).style.display = 'none'
                            }}
                          />
                          <span className="font-medium text-gray-900">{target.avatar.name}</span>
                        </div>
                      ) : (
                        <span className="text-gray-300">&mdash;</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {target.elevenlabsVoiceId ? <span title={target.elevenlabsVoiceId}>{target.elevenlabsVoiceId.slice(0, 12)}...</span> : <span className="text-gray-300">&mdash;</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{target.minRating}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{target.minSessions}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(target)} className="text-gray-600 hover:text-gray-800 hover:bg-gray-100">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleClearCache(target.id)}
                          disabled={clearingCacheId === target.id}
                          title="Clear welcome cache"
                          className="text-gray-600 hover:text-gray-800 hover:bg-gray-100"
                        >
                          <RefreshCw className={`h-4 w-4 ${clearingCacheId === target.id ? 'animate-spin' : ''}`} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(target.id)} disabled={deletingId === target.id} className="text-red-600 hover:text-red-800 hover:bg-red-50">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Create/Edit Target Modal */}
      <CreateTargetModal isOpen={isModalOpen} onClose={handleCloseModal} organisation={organisation} modules={modules} onSuccess={handleModalSuccess} editTarget={editingTarget} />
    </div>
  )
}

export default TargetsTab
